import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Microscope, LineChart, Gavel } from "lucide-react";
import { Leaf, ShieldCheck, Award, Rocket, Eye, Zap, Gem, Timer } from "lucide-react";
import { heroBanner, pillars, aboutHome, valuePoints, services, gallery } from "../mock";
import MVV from "../components/MVV";
import useSeo from "../hooks/useSeo";

const iconMap = { Leaf, ShieldCheck, Award, Rocket, Eye, Zap, Gem, Timer };
const svcIcon = (id) => (id === "research" ? Microscope : id === "finance" ? LineChart : Gavel);

const Home = () => {
  useSeo({
    title: "Antiques, Auction, Binoculars & Advisory",
    description:
      "Astoria Solutions is a trusted advisory firm for antiques, auctions, binoculars, super antique curation and RP Products — powered by decision-grade research and finance expertise.",
    path: "/",
    keywords: "Astoria Solutions, antiques, auction, binoculars, super antique, RP Products, rare bullion, research advisory, finance advisory, heritage auction, provenance",
  });
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBanner.bgImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/85 via-neutral-950/70 to-neutral-950/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-36">
          <p className="text-teal-400 font-semibold tracking-wider text-sm md:text-base">
            {heroBanner.tagline}
          </p>
          <h1 className="mt-4 text-4xl md:text-6xl font-extrabold text-white leading-tight max-w-3xl">
            {heroBanner.title[0]}
            <br />
            {heroBanner.title[1]}
          </h1>
          <p className="mt-6 text-neutral-200 max-w-xl leading-relaxed">{heroBanner.subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/about"
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Read More <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-white/40 hover:border-teal-400 text-white hover:text-teal-400 font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Get A Quote
            </Link>
          </div>
        </div>

        {/* Pillars overlapping */}
        <div className="relative max-w-7xl mx-auto px-6 -mb-16 md:-mb-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white rounded-2xl shadow-2xl p-2 md:p-4 translate-y-8 md:translate-y-16">
            {pillars.map((p) => {
              const Icon = iconMap[p.icon];
              return (
                <div
                  key={p.title}
                  className="group text-center p-6 rounded-xl hover:bg-neutral-50 transition-colors"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-neutral-100 group-hover:bg-teal-100 flex items-center justify-center transition-colors">
                    <Icon className="w-6 h-6 text-neutral-700 group-hover:text-teal-600" />
                  </div>
                  <h3 className="mt-4 font-bold text-neutral-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="bg-white pt-32 md:pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative grid grid-cols-2 gap-4">
            <img src={aboutHome.images[0]} alt="about1" className="rounded-2xl object-cover h-56 w-full shadow-md" />
            <img src={aboutHome.images[1]} alt="about2" className="rounded-2xl object-cover h-56 w-full shadow-md mt-8" />
            <img src={aboutHome.images[2]} alt="about3" className="rounded-2xl object-cover h-56 w-full shadow-md col-span-2" />
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-teal-500 text-white rounded-full w-32 h-32 flex flex-col items-center justify-center shadow-xl border-4 border-white">
              <span className="text-3xl font-extrabold">{aboutHome.years}</span>
              <span className="text-[10px] text-center px-2 leading-tight mt-1">{aboutHome.yearsLabel}</span>
            </div>
          </div>
          <div>
            <p className="text-teal-500 font-bold tracking-widest text-sm">ABOUT US</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-neutral-900 leading-tight">
              {aboutHome.heading}
            </h2>
            <p className="mt-5 text-neutral-600 leading-relaxed">{aboutHome.body1}</p>
            <p className="mt-3 text-neutral-600 leading-relaxed">{aboutHome.body2}</p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-2 text-teal-600 font-semibold hover:gap-3 transition-all"
            >
              READ MORE <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* VALUE POINTS */}
      <section className="relative bg-neutral-950 text-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${aboutHome.images[2]})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/95 to-neutral-950/60" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            {valuePoints.map((v) => {
              const Icon = iconMap[v.icon];
              return (
                <div key={v.title} className="flex gap-5">
                  <div className="w-14 h-14 shrink-0 rounded-xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">{v.title}</h3>
                    <p className="mt-1 text-neutral-300 leading-relaxed text-sm">{v.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="hidden lg:block" />
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="bg-neutral-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <p className="text-teal-500 font-bold tracking-widest text-sm">WHAT WE OFFER</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-neutral-900">
              Expert services tailored to your needs
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s) => {
              const Icon = svcIcon(s.id);
              return (
                <div key={s.id} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-neutral-100">
                  <div className="relative h-52 overflow-hidden">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-teal-500 flex items-center justify-center shadow-lg">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-neutral-900">{s.title}</h3>
                    <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.description}</p>
                    <Link to="/services" className="mt-4 inline-flex items-center gap-1 text-teal-600 font-semibold text-sm hover:gap-2 transition-all">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <p className="text-teal-500 font-bold tracking-widest text-sm">GALLERY</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-neutral-900">Our Recent Work</h2>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-4">
            {gallery.map((src, i) => (
              <div key={i} className="group relative overflow-hidden rounded-2xl aspect-square">
                <img src={src} alt={`gallery ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-neutral-950/0 group-hover:bg-neutral-950/40 transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <MVV />

      {/* CTA */}
      <section className="bg-neutral-950 border-t border-neutral-900">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <h3 className="text-3xl md:text-4xl font-extrabold text-white">Ready to unlock your next chapter?</h3>
          <p className="mt-4 text-neutral-400 max-w-2xl mx-auto">
            Partner with ASTORIA SOLUTIONS to transform ideas into intelligent, scalable outcomes.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3 rounded-full transition-colors"
          >
            Talk to our team <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Home;
